import Papa from 'papaparse';
import { ClientType } from '../types';

/**
 * Export options interface matching the modal
 */
export interface ExportOptions {
  format: 'csv' | 'xlsx';
  includePersonalInfo: boolean;
  includeFinancialInfo: boolean;
  includeCrmData: boolean;
  includePolicyInfo: boolean;
  includeComparisons: boolean;
  includeFederalTSP: boolean;
  includeFegli: boolean;
  jsonHandling: 'stringify' | 'flatten';
  exportMode?: 'single' | 'multiple';
}

/**
 * CSV Exporter utility class using PapaParse
 * Handles exporting client data to CSV format with field selection
 */
export class CSVExporter {
  /**
   * Export single client to CSV
   */
  static exportClient(
    client: ClientType,
    options: ExportOptions,
    filename?: string
  ): void {
    const data = this.prepareClientData(client, options);
    const csv = Papa.unparse([data], {
      quotes: true, // Wrap fields in quotes for safety
      delimiter: ',',
      header: true,
      newline: '\n',
    });

    const finalFilename = filename || this.generateFilename(client);
    this.downloadCSV(csv, finalFilename);
  }

  /**
   * Export multiple clients to CSV
   */
  static exportClients(
    clients: ClientType[],
    options: ExportOptions,
    filename?: string
  ): void {
    const data = clients.map(client => this.prepareClientData(client, options));
    const csv = Papa.unparse(data, {
      quotes: true,
      delimiter: ',',
      header: true,
      newline: '\n',
    });

    const finalFilename = filename || this.generateBulkFilename(clients.length);
    this.downloadCSV(csv, finalFilename);
  }

  /**
   * Prepare client data based on selected options
   */
  private static prepareClientData(
    client: ClientType,
    options: ExportOptions
  ): Record<string, any> {
    const data: Record<string, any> = {};

    // Always include base fields
    data.id = client.id;
    data.user_id = client.user_id;
    data.type = client.type;
    data.status = client.status;
    data.created_at = client.createdAt;

    // Personal Information
    if (options.includePersonalInfo) {
      data.first_name = client.firstName;
      data.last_name = client.lastName;
      data.email = client.email;
      data.phone_number = client.phoneNumber;
      data.mobile_phone = client.mobilePhone;
      data.date_of_birth = client.dateOfBirth;
      data.gender = client.gender;
      data.state_province = client.stateProvince;
      data.residence_type = client.residenceType;
      data.address = client.address;
      data.city = client.city;
      data.zip_code = client.zipCode;
      data.spouses_name = client.spousesName;
      data.spouses_date_of_birth = client.spousesDateOfBirth;
      data.related_client_id = client.related_client_id;
      data.dependents = client.dependents;
    }

    // CRM Data
    if (options.includeCrmData) {
      data.lifecycle_stage = client.lifecycleStage;
      data.last_lifecycle_update = client.last_lifecycle_update;
      data.last_contact_date = client.lastContactDate;
      data.preferred_contact_method = client.preferredContactMethod;
      data.contact_source = client.contactSource;
      data.federal_agency = client.federalAgency;
    }

    // Financial Information - Core
    if (options.includeFinancialInfo) {
      data.target_retirement_age = client.targetRetirementAge;
      data.target_retirement_month = client.targetRetirementMonth;
      data.life_expectancy_age = client.lifeExpectancyAge;
      data.interest_rate_prior_to_retirement = client.interestRatePriorToRetirement;
      data.interest_rate_in_retirement = client.interestRateInRetirement;
      data.annually_increase_savings_with_raises = client.annuallyIncreaseSavingsWithRaises;
      data.gross_income = client.grossIncome;
      data.pay_periods_annual = client.payPeriodsAnnual;
      data.expected_annual_raise = client.expectedAnnualRaise;
      data.start_payments = client.startPayments;
      data.calculation_method = client.calculationMethod;
      data.years_income_ssi = client.yearsIncomeSsi;
      data.maximum_retirement_cola = client.maximumRetirementCola;

      // Handle JSON fields - deductions and additionalIncome
      if (options.jsonHandling === 'flatten') {
        if (client.deductions) {
          const deductions = this.tryParseJSON(client.deductions);
          if (deductions && typeof deductions === 'object') {
            Object.entries(this.flattenJSON(deductions, 'deduction')).forEach(([key, value]) => {
              data[key] = value;
            });
          }
        }
        if (client.additionalIncome) {
          const additionalIncome = this.tryParseJSON(client.additionalIncome);
          if (additionalIncome && typeof additionalIncome === 'object') {
            Object.entries(this.flattenJSON(additionalIncome, 'additional_income')).forEach(([key, value]) => {
              data[key] = value;
            });
          }
        }
      } else {
        data.deductions = client.deductions;
        data.additional_income = client.additionalIncome;
      }

      // Federal-specific fields
      if (client.type === 'federal') {
        data.retirement_plan = client.retirementPlan;
        data.fers_plan = client.fersPlan;
        data.service_computation_date = client.serviceComputationDate;
        data.start_retirement_dist_at = client.startRetirementDistAt;
        data.age_to_start_distributions_at = client.ageToStartDistributionsAt;
        data.determine_social_security_method = client.determineSocialSecurityMethod;
        data.monthly_income_at_62 = client.monthlyIncomeAt62;
        data.supplement_has_annual_cola = client.supplementHasAnnualCola;
        data.include_annual_leave_benefit = client.includeAnnualLeaveBenefit;
        data.include_military_service = client.includeMilitaryService;
        data.contribution_method = client.contributionMethod;
        data.pretax_current_balance = client.pretaxCurrentBalance;
        data.contribution_per_pay = client.contributionPerPay;
        data.preretire_rate_of_return = client.preretireRateOfReturn;
        data.distribution_method = client.distributionMethod;
        data.distribution_start_method = client.distributionStartMethod;
        data.distribution_start_method_age = client.distributionStartMethodAge;
        data.income_model = client.incomeModel;
        data.needed_as_percentage = client.neededAsPercentage;
        data.annualized_cola_adjustment = client.annualizedColaAdjustment;
        data.allocation_method = client.allocationMethod;
        data.contribution_method_tsp = client.contributionMethodTSP;
        data.tsp_calculation_method = client.tspCalculationMethod;
        data.include_roth_account = client.includeRothAccount;
      }

      // 401k-specific fields
      if (client.type === '401k') {
        data.t_401k_type = client.t_401k_type;
        data.t_401k_current_salary = client.t_401k_currentSalary;
        data.t_401k_pay_periods_per_year = client.t_401k_payPeriodsPerYear;
        data.t_401k_monthly_contribution_percent = client.t_401k_monthlyContributionPercent;
        data.t_401k_employer_match_percent = client.t_401k_employerMatchPercent;
        data.t_401k_salary_limit_for_employer_match = client.t_401k_salaryLimitForEmployerMatch;
        data.t_401k_annual_management_fee = client.t_401k_annualManagementFee;
        data.t_401k_self_directed = client.t_401k_selfDirected;
        data.t_401k_estimated_early_withdrawals = client.t_401k_estimatedEarlyWithdrawals;
        data.t_401k_early_withdrawal_penalty = client.t_401k_earlyWithdrawalPenalty;
        data.t_401k_existing_401k_value = client.t_401k_existing401kValue;
      }

      // 403b-specific fields
      if (client.type === '403b') {
        data.t_403b_current_age = client.t_403b_currentAge;
        data.t_403b_planned_retirement_age = client.t_403b_plannedRetirementAge;
        data.t_403b_life_expectancy = client.t_403b_lifeExpectancy;
        data.t_403b_current_balance = client.t_403b_currentBalance;
        data.t_403b_annual_contribution = client.t_403b_annualContribution;
        data.t_403b_employer_match_percentage = client.t_403b_employerMatchPercentage;
        data.t_403b_employer_match_cap = client.t_403b_employerMatchCap;
        data.t_403b_expected_annual_return_rate = client.t_403b_expectedAnnualReturnRate;
        data.t_403b_inflation_rate = client.t_403b_inflationRate;
        data.t_403b_contribution_frequency = client.t_403b_contributionFrequency;
        data.t_403b_annual_salary = client.t_403b_annualSalary;
        data.t_403b_contribution_percentage = client.t_403b_contributionPercentage;
        data.t_403b_desired_annual_income_in_retirement = client.t_403b_desiredAnnualIncomeInRetirement;
        data.t_403b_withdrawal_rate = client.t_403b_withdrawalRate;
        data.t_403b_payout_period = client.t_403b_payoutPeriod;
        data.t_403b_start_withdrawal_age = client.t_403b_startWithdrawalAge;
        data.t_403b_marginal_tax_rate_in_retirement = client.t_403b_marginalTaxRateInRetirement;
        data.t_403b_state_tax_rate = client.t_403b_stateTaxRate;
        data.t_403b_post_retirement_return_rate = client.t_403b_postRetirementReturnRate;
        data.t_403b_social_security_income = client.t_403b_socialSecurityIncome;
        data.t_403b_other_retirement_income_sources = client.t_403b_otherRetirementIncomeSources;
        data.t_403b_early_withdrawal_penalty_percentage = client.t_403b_earlyWithdrawalPenaltyPercentage;
        data.t_403b_age_at_early_withdrawal = client.t_403b_ageAtEarlyWithdrawal;
        data.t_403b_exemptions_from_early_withdrawal_penalty = client.t_403b_exemptionsFromEarlyWithdrawalPenalty;
        data.t_403b_irs_contribution_limit = client.t_403b_irsContributionLimit;
        data.t_403b_catch_up_contributions = client.t_403b_catchUpContributions;
        data.t_403b_rmd_start_age = client.t_403b_rmdStartAge;
        data.t_403b_beneficiary_info_for_rmds = client.t_403b_beneficiaryInfoForRMDs;
        data.t_403b_loan_balance = client.t_403b_loanBalance;
        data.t_403b_loan_repayment_term = client.t_403b_loanRepaymentTerm;
        data.t_403b_loan_interest_rate = client.t_403b_loanInterestRate;
        data.t_403b_investment_mix = client.t_403b_investmentMix;
        data.t_403b_expense_ratio = client.t_403b_expenseRatio;
        data.t_403b_administrative_fees = client.t_403b_administrativeFees;
        data.t_403b_employer_contribution_vesting_percentage = client.t_403b_employerContributionVestingPercentage;
        data.t_403b_years_to_full_vesting = client.t_403b_yearsToFullVesting;
        data.t_403b_cola_percentage = client.t_403b_colaPercentage;
      }
    }

    // Federal TSP Fields
    if (options.includeFederalTSP && client.type === 'federal') {
      // Pre-Tax Account
      data.pretax_current_balance = client.pretax_currentBalance;
      data.pretax_contribution_per_paycheck = client.pretax_contributionPerPaycheck;
      data.pretax_pre_retire_rate_of_return = client.pretax_preRetireRateOfReturn;
      data.pretax_future_contributions_method = client.pretax_futureContributionsMethod;
      data.pretax_annual_increase_method = client.pretax_annualIncreaseMethod;
      data.pretax_distribution_method = client.pretax_distributionMethod;
      data.pretax_rate_of_return = client.pretax_rateOfReturn;
      data.pretax_distribution_start_method = client.pretax_distributionStartMethod;

      // Roth Account
      data.roth_current_balance = client.roth_currentBalance;
      data.roth_contribution_per_paycheck = client.roth_contributionPerPaycheck;
      data.roth_pre_retire_rate_of_return = client.roth_preRetireRateOfReturn;
      data.roth_future_contributions_method = client.roth_futureContributionsMethod;
      data.roth_annual_increase_method = client.roth_annualIncreaseMethod;
      data.roth_distribution_method = client.roth_distributionMethod;
      data.roth_rate_of_return = client.roth_rateOfReturn;
      data.roth_distribution_start_method = client.roth_distributionStartMethod;

      // TSP Allocations
      if (options.jsonHandling === 'flatten' && client.federal_tsp_allocations) {
        const allocations = this.tryParseJSON(client.federal_tsp_allocations);
        if (allocations && typeof allocations === 'object') {
          Object.entries(this.flattenJSON(allocations, 'tsp_allocation')).forEach(([key, value]) => {
            data[key] = value;
          });
        }
      } else {
        data.federal_tsp_allocations = client.federal_tsp_allocations;
      }

      // Unused Sick Leave
      if (options.jsonHandling === 'flatten' && client.unused_sick_leave) {
        const sickLeave = this.tryParseJSON(client.unused_sick_leave);
        if (sickLeave && typeof sickLeave === 'object') {
          Object.entries(this.flattenJSON(sickLeave, 'sick_leave')).forEach(([key, value]) => {
            data[key] = value;
          });
        }
      } else {
        data.unused_sick_leave = client.unused_sick_leave;
      }
    }

    // FEGLI Insurance
    if (options.includeFegli && client.type === 'federal') {
      data.fegli_basic_coverage = client.fegli_basicCoverage;
      data.fegli_postal_employee = client.fegli_postalEmployee;
      data.fegli_reduction_at_age_65 = client.fegli_reductionAtAge65;
      data.fegli_option_a = client.fegli_optionA;
      data.fegli_option_b = client.fegli_optionB;
      data.fegli_option_b_multiplier = client.fegli_optionBMultiplier;
      data.fegli_option_b_age_65_reduction = client.fegli_optionBAge65Reduction;
      data.fegli_option_c = client.fegli_optionC;
      data.fegli_option_c_multiplier = client.fegli_optionCMultiplier;
      data.fegli_option_c_include_spouse = client.fegli_optionCIncludeSpouse;
      data.fegli_option_c_age_65_reduction = client.fegli_optionCAge65Reduction;
    }

    // Policy Information
    if (options.includePolicyInfo) {
      data.policy_provider = client.policy_provider;
      data.annuity_type = client.annuity_type;
      data.rollover_amount = client.rollover_amount;
      data.policy_start_date = client.policy_start_date;
      data.policy_end_date = client.policy_end_date;
      data.policy_status = client.policy_status;
      data.agent_commission = client.agent_commission;
      data.commission_approved = client.commission_approved;
    }

    // Comparisons
    if (options.includeComparisons) {
      if (options.jsonHandling === 'flatten' && client.comparisons) {
        const comparisons = this.tryParseJSON(client.comparisons);
        if (comparisons && typeof comparisons === 'object') {
          Object.entries(this.flattenJSON(comparisons, 'comparison')).forEach(([key, value]) => {
            data[key] = value;
          });
        }
      } else {
        data.comparisons = client.comparisons;
      }
    }

    return data;
  }

  /**
   * Try to parse JSON string, return null if invalid
   */
  private static tryParseJSON(jsonString: string | undefined): any {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return null;
    }
  }

  /**
   * Flatten nested JSON objects into dot notation
   */
  private static flattenJSON(
    obj: any,
    prefix = ''
  ): Record<string, string> {
    const flattened: Record<string, string> = {};

    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}_${key}` : key;

      if (value === null || value === undefined) {
        flattened[newKey] = '';
      } else if (Array.isArray(value)) {
        flattened[newKey] = JSON.stringify(value);
      } else if (typeof value === 'object') {
        Object.assign(flattened, this.flattenJSON(value, newKey));
      } else {
        flattened[newKey] = String(value);
      }
    }

    return flattened;
  }

  /**
   * Download CSV file to user's computer
   */
  private static downloadCSV(csv: string, filename: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Generate filename for single client export
   */
  private static generateFilename(client: ClientType): string {
    const date = new Date().toISOString().split('T')[0];
    const firstName = (client.firstName || 'Client').replace(/\s+/g, '_');
    const lastName = (client.lastName || '').replace(/\s+/g, '_');
    const name = lastName ? `${firstName}_${lastName}` : firstName;
    return `Client_${name}_${date}.csv`;
  }

  /**
   * Generate filename for bulk export
   */
  private static generateBulkFilename(count: number): string {
    const date = new Date().toISOString().replace('T', '_').replace(/:/g, '-').split('.')[0];
    return `Clients_Export_${count}_${date}.csv`;
  }
}

